/** SUNTRONIX ONE — Merge Duplicate Assets wizard.
 *
 *  Opens from the Data Quality Control Centre (or from the Passport
 *  when a 409 duplicate_serial is returned).  Two-step wizard:
 *    1. Pick the primary (survivor) between the two matched assets.
 *    2. Confirm the reason so the merge is auditable.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { GitMerge, Check, Loader2, ArrowRight } from "lucide-react";

function AssetSummary({ asset, chosen, onPick, testIdPrefix }) {
  if (!asset) return null;
  const fields = [
    ["Serial", asset.serial],
    ["Asset tag", asset.asset_tag],
    ["Lifecycle", asset.lifecycle_state],
    ["Employee", asset.current_employee_id],
    ["Warehouse", asset.current_warehouse_id],
    ["Warranty", asset.warranty_end],
    ["Purchase", asset.purchase_date],
  ];
  return (
    <div
      onClick={onPick}
      data-testid={`${testIdPrefix}-${asset.id}`}
      className={`cursor-pointer border rounded-md p-3 transition
                    ${chosen ? "border-emerald-500 bg-emerald-50/50 ring-2 ring-emerald-200"
                              : "border-slate-200 hover:border-slate-300 bg-white"}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-semibold tracking-widest uppercase text-slate-500">
          {chosen ? "Survivor" : "Click to keep this one"}
        </div>
        {chosen && <Check className="h-4 w-4 text-emerald-600" />}
      </div>
      <div className="mt-2 text-[13px] font-mono">{asset.asset_tag || "—"}</div>
      <div className="text-[11px] font-mono text-slate-600">{asset.serial || "—"}</div>
      <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
        {fields.map(([k, v]) => (
          <div key={k} className="min-w-0">
            <div className="text-slate-500 uppercase tracking-wider text-[9px]">{k}</div>
            <div className="text-slate-800 truncate" title={v || "—"}>{v || "—"}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function MergeDuplicatesDialog({
  open, onOpenChange, seedAssetId, siblingAssetIds = [], onMerged,
}) {
  const [candidates, setCandidates] = useState([]);   // {id, ...asset}
  const [primaryId, setPrimaryId] = useState(null);
  const [duplicateId, setDuplicateId] = useState(null);
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [merging, setMerging] = useState(false);

  useEffect(() => {
    if (!open || !seedAssetId) return;
    let cancelled = false;
    setLoading(true);
    setReason("");
    (async () => {
      try {
        // Load the seed + any duplicates the server knows about, plus
        // whatever siblings the caller pre-selected.
        const [seed, dupes] = await Promise.all([
          api.get(`/assets/${seedAssetId}`),
          api.get(`/assets/${seedAssetId}/duplicates`),
        ]);
        if (cancelled) return;
        const map = new Map();
        map.set(seed.data.id, seed.data);
        for (const d of (dupes.data.items || [])) map.set(d.id, d);
        // If the DQ page passed extra sibling IDs (from a duplicate cluster),
        // fetch them best-effort so all cluster members show.
        for (const sid of siblingAssetIds) {
          if (map.has(sid)) continue;
          try {
            const { data } = await api.get(`/assets/${sid}`);
            map.set(data.id, data);
          } catch { /* ignore */ }
        }
        const items = Array.from(map.values());
        setCandidates(items);
        setPrimaryId(seedAssetId);
        const firstOther = items.find((x) => x.id !== seedAssetId);
        setDuplicateId(firstOther?.id || null);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Could not load candidates");
      } finally {
        !cancelled && setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, seedAssetId, siblingAssetIds]);

  const primary = useMemo(
    () => candidates.find((a) => a.id === primaryId), [candidates, primaryId]);
  const duplicate = useMemo(
    () => candidates.find((a) => a.id === duplicateId), [candidates, duplicateId]);

  const swap = () => {
    if (!primaryId || !duplicateId) return;
    setPrimaryId(duplicateId);
    setDuplicateId(primaryId);
  };

  const pickPrimary = (id) => {
    if (id === duplicateId) {
      // If the user clicks on the current duplicate, swap them
      setDuplicateId(primaryId);
    }
    setPrimaryId(id);
  };

  const doMerge = async () => {
    if (!primaryId || !duplicateId) {
      toast.error("Pick both a primary and a duplicate.");
      return;
    }
    if (!reason.trim()) {
      toast.error("A reason is required for the audit trail.");
      return;
    }
    setMerging(true);
    try {
      const { data } = await api.post(`/assets/${primaryId}/merge`, {
        duplicate_id: duplicateId,
        reason: reason.trim(),
        copy_missing: true,
      });
      toast.success(
        `Merged · ${data.filled_fields?.length || 0} field(s) folded, `
        + `${data.events_rewired || 0} event(s) rewired.`);
      onMerged?.(data);
      onOpenChange(false);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "object" ? (d.message || "Merge failed")
                                              : (d || "Merge failed"));
    } finally { setMerging(false); }
  };

  const otherCandidates = candidates.filter(
    (a) => a.id !== primaryId && a.id !== duplicateId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="merge-duplicates-dialog"
        className="max-w-3xl">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <GitMerge className="h-4 w-4 text-slate-600" />
            <DialogTitle>Merge duplicate assets</DialogTitle>
          </div>
          <div className="text-[12px] text-slate-500">
            The survivor keeps its identifiers. Any missing purchase, warranty,
            employee, warehouse or location values are folded in from the
            duplicate before it is deactivated.
          </div>
        </DialogHeader>

        {loading && (
          <div className="py-8 text-center text-slate-400 text-[12px]">
            Loading duplicate candidates…
          </div>
        )}

        {!loading && candidates.length < 2 && (
          <div className="py-8 text-center text-slate-500 text-[12px]"
                data-testid="merge-no-candidates">
            No other assets share this serial or tag any more.
          </div>
        )}

        {!loading && candidates.length >= 2 && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 items-stretch">
              <AssetSummary
                asset={primary} chosen
                onPick={() => {}}
                testIdPrefix="merge-primary" />
              <div className="hidden md:flex items-center justify-center">
                <Button size="sm" variant="ghost" onClick={swap}
                  data-testid="merge-swap">
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
              <AssetSummary
                asset={duplicate} chosen={false}
                onPick={() => pickPrimary(duplicateId)}
                testIdPrefix="merge-duplicate" />
            </div>

            {otherCandidates.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">
                  Other candidates
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {otherCandidates.map((a) => (
                    <button
                      key={a.id}
                      onClick={() => setDuplicateId(a.id)}
                      data-testid={`merge-candidate-${a.id}`}
                      className="text-left border border-slate-200 rounded p-2 hover:border-slate-400 bg-white">
                      <div className="text-[12px] font-mono">{a.asset_tag}</div>
                      <div className="text-[11px] font-mono text-slate-500">{a.serial}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div>
              <div className="text-[10px] font-semibold tracking-widest uppercase text-amber-800 mb-1">
                Reason (required)
              </div>
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Same physical device registered twice by bulk upload"
                className="border-amber-300 focus-visible:ring-amber-500"
                data-testid="merge-reason-input"
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={merging}
            data-testid="merge-cancel">
            Cancel
          </Button>
          <Button
            onClick={doMerge}
            disabled={merging || loading || candidates.length < 2 || !reason.trim()}
            data-testid="merge-confirm">
            {merging ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                       : <GitMerge className="h-3.5 w-3.5 mr-1" />}
            Merge into survivor
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
